import { FormEvent, useRef, useState } from 'react';

import { useRouteRef } from '@backstage/core-plugin-api';

import {
  FormFieldsHandle,
  extractLastNameComponent,
  useDialog,
  useNavigate,
} from '@spotify/confidence-core-react';
import { useDefaultOwner } from '@spotify/plugin-confidence-iam-react';
import {
  MeasurementFormValues,
  defaultSpecs,
  toMeasurementInput,
  useCreateMeasurement,
} from '@spotify/plugin-confidence-metrics-react';

import { measurementRouteRef } from '../../../routes';

export const useCreateMeasurementDialog = () => {
  const navigate = useNavigate();
  const measurementRoute = useRouteRef(measurementRouteRef);
  const { closeDialog } = useDialog();

  const [owner] = useDefaultOwner();

  const [step, setStep] = useState(0);

  const formFieldsRef = useRef<FormFieldsHandle<MeasurementFormValues>>(null);

  const { createMeasurement, isCreating } = useCreateMeasurement({
    onError: 'post-alert',
    onComplete: created => {
      if (created?.name) {
        navigate(
          measurementRoute({
            id: extractLastNameComponent(created.name) || '',
          }),
        );
        closeDialog();
      }
    },
  });

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (isCreating) return;

    const formFields = formFieldsRef.current;
    if (!formFields) return;

    if (!formFields.isValid) {
      formFields.setShowErrors(true);
      return;
    }

    const { metricType, ...rest } = formFields.values;

    await createMeasurement(
      toMeasurementInput({
        ...rest,
        labels: [
          ...(rest.labels ?? []),
          { key: 'metricType', value: metricType },
        ],
      }),
    );
  };

  const initialValues: Partial<MeasurementFormValues> = {
    displayName: '',
    description: '',
    owner: owner ?? '',
    entity: '',
    factTable: '',
    metricType: 'unknown',
    typeSpec: defaultSpecs.unknown,
  };

  return {
    step,
    setStep,
    formFieldsRef,
    initialValues,
    handleSubmit,
    loading: isCreating,
  };
};
